import { useMemo } from "react";
import { useLocation, useMatches } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuthStore } from "@/features/auth/hooks";
import { useCan } from "@/hooks/useCan/useCan";
import { PATHS } from "@/lib/router";
import { NotFoundPage } from "@/components/common";

const AccessDenied = () => (
  <div className="w-full flex justify-center py-12">
    <div className="border border-dashed rounded-lg p-8 max-w-md text-center">
      <div className="text-4xl mb-3 flex justify-center">
        <Lock className="w-8 h-8 text-emerald-500" />
      </div>
      <h3 className="text-xl font-semibold text-gray-500">
        View Permission Required
      </h3>
      <p className="mt-2 text-gray-500">
        You are not allowed to access this section.
      </p>
    </div>
  </div>
);

export const RouteGuard = ({ children }) => {
  const matches = useMatches();
  const location = useLocation();
  const { user, userGroup } = useAuthStore();
  const userPermissions = user?.permissions || [];
  const hasReportId = new URLSearchParams(location.search).has("reportId");
  const isDelegationRoute =
    location.pathname.startsWith("/esg/report/") ||
    location.pathname === PATHS.DATA_ENTRY;

  const permission = useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i -= 1) {
      const p = matches[i]?.handle?.permission;
      if (p) return p;
    }
    return null;
  }, [matches]);

  const isDefaultView = useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i -= 1) {
      const isDefault = matches[i]?.handle?.defaultView;
      if (isDefault) return true;
    }
    return false;
  }, [matches]);

  const blacklist = useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i -= 1) {
      const list = matches[i]?.handle?.blacklistGroup;
      if (list?.length) return list;
    }
    return null;
  }, [matches]);

  const allowedRoles = useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i -= 1) {
      const list = matches[i]?.handle?.allowedRoles;
      if (list?.length) return list;
    }
    return null;
  }, [matches]);

  const companyFeatureFlag = useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i -= 1) {
      const flag = matches[i]?.handle?.companyFeatureFlag;
      if (flag) return flag;
    }
    return null;
  }, [matches]);

  const isBlacklisted = Boolean(
    userGroup && Array.isArray(blacklist) && blacklist.includes(userGroup),
  );
  const isRoleAllowed =
    !Array.isArray(allowedRoles) ||
    allowedRoles.length === 0 ||
    allowedRoles.includes(user?.role);
  const isCompanyFeatureEnabled =
    !companyFeatureFlag || user?.[companyFeatureFlag] !== false;

  const { allowed } = useCan({
    userPermissions,
    ...(permission || {}),
    enabled: Boolean(permission),
  });

  if (isBlacklisted) return <NotFoundPage />;
  if (!isRoleAllowed) return <NotFoundPage />;
  if (!isCompanyFeatureEnabled) return <NotFoundPage />;
  if (isDelegationRoute && hasReportId) {
    return children;
  }
  if (isDefaultView) return children;
  if (!permission) return children;
  if (allowed) return children;

  return <NotFoundPage />;
};
